import {
  HttpErrorCode,
  StripeErrorCause,
  StripeErrorCode,
} from '@copera/common/error';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';
import { useLocales } from 'src/locales';
import { useListener } from 'src/services/EventBus/useListener';

const defaultMessagesKeys: Record<HttpErrorCode, string> = {
  [HttpErrorCode.MISSING_PARAMS]: 'error.http.missing_params',
  [HttpErrorCode.NOT_FOUND]: 'error.http.not_found',
  [HttpErrorCode.WRONG_PASSWORD]: 'error.http.wrong_password',
  [HttpErrorCode.FORBIDDEN]: 'error.http.forbidden',
  [HttpErrorCode.UNAUTHORIZED]: 'error.http.unauthorized',
  [HttpErrorCode.INVALID]: 'error.http.invalid',
  [HttpErrorCode.UNPROCESSABLE_ENTITY]: 'error.http.unprocessable_entity',
  [HttpErrorCode.ALREADY_EXISTS]: 'error.http.already_exists',
  [HttpErrorCode.UNKNOWN]: 'error.http.unknown',
  [HttpErrorCode.INVALID_CONTENT]: 'error.http.invalid_content',
  [HttpErrorCode.LIMIT_EXCEEDED]: 'error.http.limit_exceeded',
  [HttpErrorCode.PAYMENT_FAILED]: 'error.http.payment_failed',
};

export const STRIPE_PAYMENT_FAILED_MAP = {
  [StripeErrorCode.CARD_DECLINED]: {
    [StripeErrorCause.GENERIC_DECLINE]: 'error.stripe.generic_decline',
    [StripeErrorCause.INSUFFICIENT_FUNDS]: 'error.stripe.insufficient_funds',
    [StripeErrorCause.LOST_CARD]: 'error.stripe.lost_card',
    [StripeErrorCause.STOLEN_CARD]: 'error.stripe.stolen_card',
    [StripeErrorCause.CARD_VELOCITY_EXCEEDED]:
      'error.stripe.card_velocity_exceeded',
  },
  [StripeErrorCode.EXPIRED_CARD]: 'error.stripe.expired_card',
  [StripeErrorCode.INCORRECT_CVC]: 'error.stripe.incorrect_cvc',
  [StripeErrorCode.PROCESSING_ERROR]: 'error.stripe.processing_error',
  [StripeErrorCode.INCORRECT_NUMBER]: 'error.stripe.incorrect_number',
  default: 'error.http.payment_failed',
} as const;

const stripeErrorToMessage = (
  code?: StripeErrorCode,
  declineCode?: StripeErrorCause,
) => {
  const entry = code && STRIPE_PAYMENT_FAILED_MAP[code];
  if (!entry) return STRIPE_PAYMENT_FAILED_MAP.default;
  return typeof entry === 'string'
    ? entry
    : (entry[declineCode!] ?? STRIPE_PAYMENT_FAILED_MAP.default);
};

const reportError = (message: string, error: Error) => {
  if (location.hostname === 'localhost') {
    throw error;
  }
  console.error(message, error);
};

export const ReactQueryErrorHandler = () => {
  const { translate } = useLocales();

  useListener(
    'ReactQueryError',
    ({ error, errors }) => {
      if (!(error instanceof AxiosError)) {
        console.error('Unexpected error', error);
        toast.error('Unexpected error');
        return;
      }

      const errorCode = error.response?.data?.code as HttpErrorCode;
      if (errorCode === HttpErrorCode.PAYMENT_FAILED) {
        const details = error.response?.data?.details ?? {};
        const translatedErrorMessage = stripeErrorToMessage(
          details?.code as StripeErrorCode,
          details?.declineCode as StripeErrorCause,
        );
        toast.error(translate(translatedErrorMessage));
        return;
      }
      const queryError = errors?.[errorCode];

      if (!queryError) {
        if (!defaultMessagesKeys[errorCode]) {
          toast.error(translate('error.http.unknown'));
          return;
        }

        const message = translate(defaultMessagesKeys[errorCode]);
        toast.error(message);

        return;
      }
      const cause = error.response?.data?.cause;

      if (typeof queryError === 'string') {
        toast.error(translate(queryError));
        if (cause) reportError('Unknown cause for error', error);
        return;
      }

      if (queryError[cause]) {
        toast.error(translate(queryError[cause] || queryError.default));
        return;
      }

      toast.error(translate(queryError.default));
      reportError('Unknown cause for error', error);
    },
    [],
  );

  return null;
};
